import { UserGroupIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router";
import React, { useEffect, useState } from "react";
import { graphql } from "~/graphql/codegen";
import { useLazyQuery } from "@apollo/client/react";
import {
  AttendeeFragment,
  MindspaceEventAttendeesQuery,
  MindspaceEventAttendeesQueryVariables,
} from "~/graphql/codegen/graphql";
import {
  Avatar,
  AvatarImage,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@mindstonehq/ui";
import { useCurrentSpace } from "~/hooks/context-hook";
import FollowListItem from "~/components/follow-list-item";
import useInfiniteScroll, { PaginatedData } from "~/hooks/infinite-scroll";
import { Trans } from "@lingui/react/macro";

const ATTENDEE_FRAGMENT = graphql(`
  fragment Attendee on Profile {
    id
    displayName
    profilePictureURL
    username
    tagLine
    following
    mine
  }
`);

const MINDSPACE_EVENT_ATTENDEES_QUERY = graphql(`
  query MindspaceEventAttendees(
    $uniqueName: String!
    $identifier: String!
    $attendeesSize: Int
    $attendeesOffset: Int
  ) {
    mindspace(identifier: $uniqueName) {
      events {
        event(identifier: $identifier) {
          attendance {
            attendees(size: $attendeesSize, offset: $attendeesOffset) {
              hasMore
              nextItem
              total
              profiles {
                ...Attendee
              }
            }
          }
          attending
        }
      }
    }
  }
`);

export default function MindspaceEventAttendees({
  attendees: attendeesMinimum,
}: {
  attendees?:
    | { id: string; displayName: string; profilePictureURL: string }[]
    | null;
}) {
  const currentSpace = useCurrentSpace();
  const [mindspaceEventAttendeesQuery] = useLazyQuery(
    MINDSPACE_EVENT_ATTENDEES_QUERY,
  );
  const [showAttendeeDialog, setShowAttendeeDialog] = React.useState(false);
  const { slug: eventSlug } = useParams();

  const [initialData, setInitialData] = useState<
    PaginatedData<AttendeeFragment, number>
  >({
    items: [],
    hasMore: true,
    nextItem: 0,
  });

  const { items, loading, hasMore, sentinelRef, setItems } = useInfiniteScroll(
    initialData,
    (offset, _signal) =>
      mindspaceEventAttendeesQuery({
        variables: {
          uniqueName: currentSpace.uniqueName,
          identifier: eventSlug || "",
          attendeesSize: 20,
          attendeesOffset: offset,
        },
      }).then((res) => {
        let attendance = res?.data?.mindspace.events?.event?.attendance;
        if (!attendance) {
          return {
            items: [],
            hasMore: false,
          };
        }
        return {
          items: attendance.attendees.profiles || [],
          hasMore: attendance.attendees.hasMore || false,
          nextItem: attendance.attendees.nextItem,
        };
      }),
  );

  useEffect(() => {
    mindspaceEventAttendeesQuery({
      variables: {
        uniqueName: currentSpace.uniqueName,
        identifier: eventSlug || "",
        attendeesSize: 20,
        attendeesOffset: 0,
      },
    }).then((res) => {
      let attendance = res?.data?.mindspace.events?.event?.attendance;
      if (!attendance) {
        return {
          items: [],
          hasMore: false,
        };
      }
      setInitialData({
        items: attendance.attendees.profiles || [],
        hasMore: attendance.attendees.hasMore || false,
        nextItem: attendance.attendees.nextItem,
      });
    });
    // Initial fetch
  }, [showAttendeeDialog]);

  const handleFollowUpdate = (id: string, following: boolean) => {
    setItems((items) =>
      items.map((profile) => {
        if (profile.id === id) {
          return {
            ...profile,
            following,
          };
        }
        return profile;
      }),
    );
  };

  return (
    <>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <h6 className="flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-1" />
            <span><Trans>Attendees</Trans></span>
          </h6>
          <button
            className="lg:hidden link text-xs ml-1"
            onClick={() => setShowAttendeeDialog(true)}
          >
            <Trans>Show all</Trans>
          </button>
        </div>
        <Dialog open={showAttendeeDialog} onOpenChange={setShowAttendeeDialog}>
          <DialogTrigger asChild>
            <button
              // onClick={() => setShowAttendeeDialog(true)}
              className="flex cursor-hover"
            >
              {attendeesMinimum?.map((attendee, index) => (
                <Avatar className={index > 0 ? "-ml-4" : ""} key={attendee.id}>
                  <AvatarImage src={attendee.profilePictureURL} />
                </Avatar>
              ))}
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle><Trans>Attendees</Trans></DialogTitle>
            </DialogHeader>
            <div className="flex flex-col flex-1 md:flex-none space-y-3 md:h-[350px] md:max-h-[350px] lg:h-[450px] lg:max-h-[450px] md: min-h-0 overflow-y-auto ">
              {items.map((user) => {
                return (
                  <FollowListItem
                    key={user.id}
                    profile={user}
                    afterFollow={() => handleFollowUpdate(user.id, true)}
                    afterUnfollow={() => handleFollowUpdate(user.id, false)}
                  />
                );
              })}
              {hasMore && <div ref={sentinelRef} className="h-1 pb-2" />}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
